import type { JSX } from 'react';
import { CHRONICLE, chronicleCount } from '../labels';
import { useChronicle } from '../useChronicle';
import { ChronicleFeed } from './ChronicleFeed';
import { ChronicleSheet } from './ChronicleSheet';

/**
 * The landing page's chronicle mount, self-contained so the page only has to place it.
 *
 * Two shapes of one panel (ui-direction §3): a sticky rail with its own scroll from `lg` up, and
 * below that a single teaser card that opens the same Sheet the header button opens.
 */

/** How much of the window the rail shows before handing the rest to the Sheet. */
const RAIL_ITEMS = 8;

export function ChronicleRail(): JSX.Element {
  const chronicle = useChronicle();
  const { count, entries } = chronicle;
  const more = entries.length > RAIL_ITEMS;

  return (
    <aside className="min-w-0" aria-label={CHRONICLE.title}>
      {/* Mobile: the teaser only. It states the count when there is one, and otherwise just names
          the panel rather than showing a number the window has not earned. */}
      <ChronicleSheet
        trigger={
          <button
            type="button"
            className="flex w-full items-center justify-between gap-3 rounded-md border border-line bg-surface px-4 py-3 text-left shadow-card hover:border-accent lg:hidden"
          >
            <span className="font-display text-h3 font-bold text-accent-deep">
              {CHRONICLE.title}
            </span>
            <span className="text-small text-ink-muted">
              {count !== null && `${chronicleCount(count)} `}›
            </span>
          </button>
        }
      />

      <div className="hidden lg:sticky lg:top-4 lg:block lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto lg:pr-0.5">
        <ChronicleFeed
          chronicle={chronicle}
          limit={RAIL_ITEMS}
          footer={
            more ? (
              <p className="mt-0 border-t border-line pt-2.5 text-small">
                <ChronicleSheet
                  trigger={
                    <button
                      type="button"
                      className="font-semibold text-accent hover:text-accent-deep"
                    >
                      {CHRONICLE.open}
                    </button>
                  }
                />
              </p>
            ) : null
          }
        />
      </div>
    </aside>
  );
}
